<template>
  <div :class="['card-chart', { fullscreen: dashboardFullscreen }]" :style="{ height: cardHeight }">
    <CardInteractive
      :title="title"
      :info="info"
      :enableFullscreen="enableFullscreen"
      :showControls="fullscreen && enableResolution"
      :isLoading="isLoading"
      headerDivider
      @toggleFullscreen="toggleFullscreen"
      @toggleShowInfo="(value) => (showingInfo = value)"
    >
      <ChartBase
        ref="chartBase"
        :class="['chart-container', { fullscreen }]"
        :height="currentHeight"
        :components="filteredComponents"
      />
      <template #footerControls>
        {{ t('dashboard__text__selection') }} :
        <CollapseControllerSummaryNew :showTimeSelection="true" :fullscreen="fullscreen" />
      </template>
    </CardInteractive>
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, onUnmounted, ref, toRefs, watch } from 'vue'
import CardInteractive from './CardInteractive.vue'
import { ChartComponent } from './ChartComponent'
import ChartBase from './ChartComponent/ChartBase.vue'
import { useI18n } from 'vue-i18n'
import { HistogramValueNew } from '@/api-client/models/indoorclimate/Contracts'
import { sharedDataKey } from '../../types'
import { Measurement } from '@/features/dashboard'
import { DataSource, useDataSources } from '@/composables/useDataSources'
import { Expression, MeasurementPoint } from '../measurements/contracts'
import { useComponentFilter } from '../measurements/useMeasurements'
import CollapseControllerSummaryNew from './CollapseController/CollapseControllerSummaryNew.vue'
import { ColorScaleConfig } from '@/utils/colorScale'
import { FormatOptions } from '../formatter'
import { createHistogramNew, HistogramConfig } from './ChartComponent/chart/histogram'

export type Props = {
  featureName: string
  title: string
  info?: string
  enableFullscreen?: boolean
  chartHeight?: string
  cardHeight?: string
  fields: string[]
  measurements: Measurement[]
  colorScale?: ColorScaleConfig
  dataFormat?: FormatOptions
}

const props = withDefaults(defineProps<Props>(), {
  chartHeight: '100%',
  cardHeight: '400px',
  colorScale: undefined,
  dataFormat: undefined,
  info: undefined
})
const sharedData = inject(sharedDataKey)
const dashboardFullscreen = computed(() => sharedData?.fullscreen ?? false)
const { measurements, chartHeight, colorScale } = toRefs(props)
const { locale, t } = useI18n()

const showingInfo = ref(false)
useComponentFilter(props.featureName, {})

const enableResolution = computed(() => true)
const chartBase = ref<InstanceType<typeof ChartBase> | undefined>(undefined)
const viewport = ref<DOMRect | undefined>(undefined)

const { getDataSources } = useDataSources()

const dataSourceIds = computed(() =>
  measurements.value.map(
    (measurement) =>
      `measurements.${props.featureName}.${measurement.name}.${measurement.field.name}.histogram.values`
  )
)

const dataSources = getDataSources<MeasurementPoint[]>(dataSourceIds)

const mergedDataSources = computed<DataSource<MeasurementPoint[]>[]>(() => {
  return dataSources?.value
    ? Object.values(dataSources.value)
        .filter((dataSource) => dataSource !== undefined)
        .map((dataSource) => dataSource!)
    : []
})

const data = computed<MeasurementPoint[]>(() => {
  return mergedDataSources.value
    .filter((ds) => ds.data !== undefined)
    .map((ds) => ds.data!)
    .flat()
})

const fields = computed<string[]>(() => measurements.value.map((m) => m.field.name))

const histogramValues = computed<HistogramValueNew[]>((): HistogramValueNew[] => {
  const m = data.value[0]
  const field = m?.fields[fields.value[0]] as number[] | undefined
  if (!field) {
    return []
  }
  return (
    field.map((v, i) => ({
      value: v,
      start: i === 0 ? null : i,
      end: i === field.length - 1 ? null : i + 1
    })) ?? []
  )
})

const min = computed<number>(
  () => (measurements.value.at(0)?.field.expression as Expression<'histogram'>).min ?? 0
)
const max = computed<number>(
  () => (measurements.value.at(0)?.field.expression as Expression<'histogram'>).max ?? 0
)
const bucketCount = computed<number>(
  () => (measurements.value.at(0)?.field.expression as Expression<'histogram'>).nbuckets ?? 0
)
const normalizedHistogramValues = computed<HistogramValueNew[]>((): HistogramValueNew[] => {
  const sum = histogramValues.value.reduce((acc, v) => acc + v.value, 0)
  const range = max.value - min.value
  const bucketWidth = range / bucketCount.value
  return histogramValues.value.map(
    (v): HistogramValueNew => ({
      start: v.start !== null ? min.value + v.start * bucketWidth : null,
      end: v.end !== null ? min.value + v.end * bucketWidth : null,
      value: v.value / sum
    })
  )
})

const isLoading = computed(() => {
  return mergedDataSources.value.every((dataSource) => dataSource.isLoading)
})

const chart = computed<HistogramConfig>(
  (): HistogramConfig => ({
    codomainFormat: {
      format: {
        type: 'intl-number',
        style: 'percent',
        maximumFractionDigits: 1
      }
    },
    domainFormat: props.dataFormat,
    valueFilterTreshold: 0.001,
    codomainTicks: {
      values: [0, 0.5, 1]
    },
    missingDataMessage: {
      en: 'Missing data',
      sv: 'Saknar data'
    },
    colorScale: colorScale.value,
    label: measurements.value[0].title,
    verticalAxis: {},
    legendColor: 'blue'
  })
)
const components = computed<ChartComponent[] | undefined>(() =>
  viewport.value
    ? createHistogramNew(chart.value, normalizedHistogramValues.value, viewport.value, locale.value)
    : undefined
)

const filteredComponents = computed<ChartComponent[]>(
  () => components.value ?? []
  // filterChartComponents('histogram', components.value ?? [], enabledLabels.value)
)

// const legendComponents = computed(
//   () =>
//     // createTimeSeriesLegendComponents(chart.value as Chart<'timeseries'>, locale.value, {
//     //   type: 'tiny'
//     // })
//     undefined
// )

// const enabledLabels = ref<Set<string>>(new Set())

const fullscreen = ref(false)

const currentHeight = computed(() => (fullscreen.value ? '600px' : chartHeight.value))

const updateViewport = () => {
  viewport.value = chartBase.value?.$el.getBoundingClientRect()
}

const viewportObserver = new ResizeObserver(updateViewport)

onUnmounted(() => {
  viewportObserver.disconnect()
})

watch(
  chartBase,
  () => {
    viewportObserver.disconnect()
    if (chartBase.value) {
      updateViewport()
      viewportObserver.observe(chartBase.value.$el)
    }
  },
  { immediate: true }
)

// watch(
//   legendComponents,
//   () => {
//     enabledLabels.value = new Set(legendComponents.value?.map((c) => c.label) ?? [])
//   },
//   { immediate: true }
// )

function toggleFullscreen(value: boolean) {
  fullscreen.value = value
}
</script>

<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/mixins.scss';

.card-chart {
  width: 100%;
}

@media screen and (min-width: 768px) {
  .card-chart {
    width: 100%;

    &.fullscreen {
      min-height: 509px;
      width: calc(50% - variables.$gap-large);
    }
  }
}

.checkbox-row {
  display: flex;
  gap: variables.$gap-xlarge;
  justify-content: left;
  flex-wrap: wrap;
  flex-direction: row;
}
.chart-container {
  display: flex;
  flex-direction: row;
}
.controls-container {
  display: flex;
  justify-content: flex-start;
  width: 200px;
  height: 100%;
}
</style>
